// src/pages/PaymentForm.jsx - プロライン決済統合 (完全修正版)
import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';
import { CreditCard, Check, Crown, Star, ArrowRight } from 'lucide-react';
import proLineService from '../services/ProLineService'; // ✅ ProLineService import 追加

const PaymentForm = () => {
  const { user, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // プロライン決済プラン
  const plans = [
    {
      id: 'pro',
      name: 'PROプラン',
      price: 3480,
      period: '月',
      description: 'プロフェッショナル向け',
      features: [
        'AI分析の完全解放',
        '成長パターン分析の全機能',
        '無制限の再診断',
        'チーム機能(3名まで)',
        'LINE連携サポート'
      ],
      popular: true
    },
    {
      id: 'business',
      name: 'Businessプラン', 
      price: 30000,
      period: '月',
      description: 'ビジネス・チーム向け',
      features: [
        'PROプランの全機能',
        'チーム招待数 無制限',
        'チーム分析ダッシュボード',
        '1on1支援システム',
        '高度な管理者機能'
      ],
      popular: false
    }
  ];

  // ✅ 修正済み: ProLineService を使用した決済処理
  const handlePayment = async (planId) => {
    if (!isAuthenticated || !user) {
      setError('ログインが必要です');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      console.log('🚀 PaymentForm.handlePayment 開始:', planId);

      // ✅ ProLineService を使用して決済URL生成
      const result = await proLineService.createCheckoutSession(planId);
      
      if (result.success && result.checkoutUrl) {
        console.log('✅ プロライン決済URL取得成功:', result.checkoutUrl);
        setSuccess(`${planId}プランの決済ページを開きます...`);
        
        // プロライン決済ページを新しいタブで開く
        setTimeout(() => {
          window.open(result.checkoutUrl, '_blank');
        }, 1000);
        
      } else {
        throw new Error('決済URL生成に失敗しました');
      }

    } catch (error) {
      console.error('❌ 決済処理エラー:', error);
      setError('決済処理でエラーが発生しました: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // 認証状態チェック
  if (!isAuthenticated) {
    return (
      <div className="max-w-md mx-auto mt-8 p-6 bg-white rounded-lg shadow-md">
        <h2 className="text-2xl font-bold text-center text-gray-900 mb-4">
          決済にはログインが必要です
        </h2>
        <p className="text-center text-gray-600 mb-6">
          プランを購入するには、まずアカウントにログインしてください。
        </p>
        <button
          onClick={() => window.location.href = '/login'}
          className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
        >
          ログインページへ
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">
          NOBISHIROコンピテンシー® プラン選択
        </h1>
        <p className="text-xl text-gray-600">
          あなたの成長とチームの成果を最大化するプランをお選びください
        </p>
      </div>

      {/* エラー・成功メッセージ */}
      {error && (
        <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
          <strong>エラー:</strong> {error}
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
          <strong>成功:</strong> {success}
        </div>
      )}

      {/* プラン一覧 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
        {plans.map((plan) => (
          <div
            key={plan.id}
            className={`relative bg-white rounded-xl shadow-lg border-2 p-8 ${
              plan.popular 
                ? 'border-blue-500 transform scale-105' 
                : 'border-gray-200'
            }`}
          >
            {plan.popular && (
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-blue-500 text-white px-4 py-1 rounded-full text-sm font-semibold flex items-center gap-1">
                  <Star className="w-4 h-4" />
                  最人気
                </span>
              </div>
            )}

            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                {plan.name}
              </h3>
              <p className="text-gray-600 mb-4">{plan.description}</p>
              <div className="text-4xl font-bold text-gray-900">
                ¥{plan.price.toLocaleString()}
                <span className="text-lg text-gray-600">/{plan.period}</span>
              </div>
            </div>

            <ul className="space-y-3 mb-8">
              {plan.features.map((feature, index) => (
                <li key={index} className="flex items-center">
                  <Check className="w-5 h-5 text-green-500 mr-3" />
                  <span className="text-gray-700">{feature}</span>
                </li>
              ))}
            </ul>

            <button
              onClick={() => handlePayment(plan.id)}
              disabled={loading}
              className={`w-full py-4 px-6 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center gap-2 ${
                plan.popular
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-900 text-white hover:bg-gray-800'
              } ${loading ? 'opacity-75 cursor-not-allowed' : 'hover:transform hover:scale-105'}`}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  処理中...
                </>
              ) : (
                <>
                  <CreditCard className="w-5 h-5" />
                  LINEで{plan.name}を購入する
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        ))}
      </div>

      {/* プロライン連携の説明 */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
        <h3 className="text-lg font-semibold text-blue-900 mb-2">
          LINE連携について
        </h3>
        <p className="text-blue-700">
          決済完了後、自動的にLINEアカウントと連携され、プロライン機能をご利用いただけます。
          <br />
          アカウントID: {proLineService.accountId}
        </p>
      </div>
    </div>
  );
};

export default PaymentForm;